import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Horizontal scroll strip with desktop arrow controls.
 *
 * Touch users get native swipe; trackpad users get native horizontal scroll;
 * desktop mouse users get visible left/right arrows AND vertical wheel ->
 * horizontal scroll mapping so the strip is reachable with any input.
 *
 * Usage:
 *   <ScrollStrip testid="weekend-desktop-scroll" theme="dark">
 *     {items.map((x) => <Card key={x.id} ... />)}
 *   </ScrollStrip>
 *
 * Props:
 *   - testid:        data-testid for the scrollable container
 *   - theme:         "dark" | "light" (default: light) — affects arrow contrast
 *   - className:     extra classes for the scroll container (defaults cover spacing)
 *   - scrollByPx:    custom scroll step (default 380, ~one card)
 */
export default function ScrollStrip({
  children,
  testid,
  theme = 'light',
  className = 'flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-6 lg:px-12 pb-2',
  scrollByPx = 380,
}) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateButtons = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth - 1;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < max);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    updateButtons();
    el.addEventListener('scroll', updateButtons, { passive: true });
    const ro = new ResizeObserver(updateButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateButtons);
      ro.disconnect();
    };
  }, [updateButtons, children]);

  // Vertical wheel -> horizontal scroll on desktop pointing devices (mouse wheels
  // only emit deltaY). We only intercept when deltaX is small AND there is room.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function onWheel(e) {
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return; // trackpad already horizontal
      const max = el.scrollWidth - el.clientWidth - 1;
      if (max <= 0) return;
      // Only intercept when there is room to scroll in the wheel direction
      if ((e.deltaY > 0 && el.scrollLeft >= max) || (e.deltaY < 0 && el.scrollLeft <= 0)) return;
      e.preventDefault();
      el.scrollBy({ left: e.deltaY, behavior: 'auto' });
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  function scroll(dir) {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * scrollByPx, behavior: 'smooth' });
  }

  const arrowBtn = theme === 'dark'
    ? 'bg-white/10 hover:bg-white/20 text-base border-white/20 backdrop-blur'
    : 'bg-white hover:bg-stone text-charcoal border-charcoal/15 shadow-soft';

  return (
    <div className="relative group">
      <div ref={ref} className={className} data-testid={testid} style={{ WebkitOverflowScrolling: 'touch' }}>
        {children}
      </div>
      {/* Desktop arrow controls — hidden on touch-only viewports via md: */}
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => scroll(-1)}
        disabled={!canLeft}
        data-testid={testid ? `${testid}-prev` : 'scroll-strip-prev'}
        className={`hidden md:flex items-center justify-center absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border ${arrowBtn} transition-opacity ${canLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => scroll(1)}
        disabled={!canRight}
        data-testid={testid ? `${testid}-next` : 'scroll-strip-next'}
        className={`hidden md:flex items-center justify-center absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border ${arrowBtn} transition-opacity ${canRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
}
